import React, { useEffect, useState } from 'react';
import { apiUpdate } from '../api/apiClient';

interface PinCellProps {
  /** Tenant uuid or numeric id — passed to apiUpdate('tenants', id, ...). */
  tenantId: string | number;
  /** Currently-saved PIN as stored on the tenant record (may be empty/null). */
  savedPin?: string | null;
  /** Width for the input — defaults match the existing inline-table cells. */
  width?: number;
  /** Called after a successful save so the parent can refresh its row state
   *  (the parent's all-tenants list typically keeps a local copy of access_pin). */
  onSaved?: (newPin: string) => void;
  /** Optional refresh hook — used by the property page to recalc counters. */
  onAfterSave?: () => void;
}

/**
 * PIN entry cell with explicit Save button + masked display.
 *
 *   • PIN is masked by default (type="password" + a 4-digit dot placeholder).
 *   • A Show/Hide toggle reveals the value temporarily.
 *   • A Save button appears only when the draft differs from the saved value,
 *     replacing the prior auto-save-on-blur behavior (which caused stale half-
 *     typed PINs to be persisted).
 *   • Save Disables while the request is in flight. Errors surface in console
 *     and revert the draft; the row stays editable so the user can retry.
 */
export const PinCell: React.FC<PinCellProps> = ({
  tenantId,
  savedPin,
  width = 80,
  onSaved,
  onAfterSave,
}) => {
  const initial = savedPin ?? '';
  const [draft, setDraft] = useState<string>(initial);
  const [shown, setShown] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // When the row's saved PIN changes from above (e.g. a refresh), sync the
  // local draft so we don't show stale data.
  useEffect(() => { setDraft(savedPin ?? ''); }, [savedPin]);

  const dirty = draft !== initial;
  const handleSave = async () => {
    setSaving(true);
    try {
      const value = draft || null;
      await apiUpdate('tenants', String(tenantId), { access_pin: value });
      onSaved?.(draft);
      onAfterSave?.();
    } catch (err) {
      console.error('Failed to save access_pin:', err);
      // revert local draft so the UI matches reality
      setDraft(savedPin ?? '');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type={shown ? 'text' : 'password'}
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={8}
        value={draft}
        placeholder="4–8"
        style={{ width }}
        className="px-1.5 py-0.5 border border-gray-300 rounded text-xs font-mono tracking-wider"
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 8))}
      />
      {/* Show / Hide toggle — only meaningful when there's a value to mask. */}
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        disabled={!draft}
        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {shown ? 'Hide' : 'Show'}
      </button>
      {/* Save button — appears only when the draft differs from saved. */}
      {dirty && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
      )}
    </div>
  );
};
